import React from 'react';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from '../../../../utils/format';
import PricingDetails, {
  PricingDataProps,
} from '../../../../components/SubscriptionPaymentDetails/PricingDetails';
import { deriveBilling, toTermTotal } from '../checkout.helpers';
import type { StripeCheckout } from '../checkout.types';
import { PriceTableSkeleton } from './CheckoutSkeletons';

const CheckoutPriceTable: React.FC<{ checkout: StripeCheckout | null }> = ({ checkout }) => {
  const { t } = useTranslation();

  // No session yet (initial load / warming) → skeleton rows.
  if (!checkout) return <PriceTableSkeleton />;

  const { total } = checkout;
  const { isYearly, billing } = deriveBilling(checkout);

  const totalMinorUnits = toTermTotal(billing.total.minorUnitsAmount, isYearly);
  // Pre-discount term sum, for the struck-through full price when a promo hits.
  const fullMinorUnits = toTermTotal(billing.subtotal.minorUnitsAmount, isYearly);
  const hasDiscount = fullMinorUnits > totalMinorUnits;

  const pricingData: PricingDataProps[] = [
    {
      label: t('Amount Due Today'),
      // `total.total` is the actual charge now — interval-correct, and 0 on
      // trials. Format via `currencyFormat` (not Stripe's `.amount` string) so
      // the € sign sits on the right, consistent with the rest of the summary.
      price: currencyFormat(total.total.minorUnitsAmount / 100),
      dataTestId: 'stripe-checkout-amount-due-today',
    },
    {
      label: t('Total'),
      price: currencyFormat(totalMinorUnits / 100),
      previousPrice: hasDiscount ? currencyFormat(fullMinorUnits / 100) : undefined,
      dataTestId: 'stripe-checkout-total-price',
    },
  ];

  // Returns bare <TableRow>s so the summary can render them inside the same
  // <Table> as the rhythm + promo rows — one table, uniform cell-border lines.
  // Struck-through full price sits left of the discounted price.
  return <PricingDetails pricingData={pricingData} />;
};

export default CheckoutPriceTable;
